// utils/profilePageTemplate.ts
// Liquid Glass amPassport - Matching React Design with ALL Technical Features

export const generateProfilePageHTML = (userData: {
  joinDate: number;
  xPostingsCount: any;
  githubInteractionsCount: any;
  userId: string;
  processId: string;
  points: number;
  currentPrimaryName?: string;
  avatarUrl?: string;
  xId?: string;
  githubId?: string;
  levelTitle?: string;
  level?: number;
  pointsBreakdown?: any;
  pointsStats?: any;
  undernameName?: string;
  undernameTransactionId?: string;
}, options: { transparent?: boolean } = {}) => {
  const isTransparent = !!options.transparent;
  // Calculate dynamic data
  const joinDate = userData.joinDate || Date.now();
  const joinDateFormatted = new Date(joinDate).toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
  
  // Verification logic - check if the URL matches the expected undername pattern
  // Since this runs server-side initially, default to unverified
  // Client-side script will update based on actual URL
  const isVerified = false;
  
  // Get top 3 point categories (matching React component logic)
  const pointsData = userData.pointsStats || userData.pointsBreakdown || {};
  const topCategories = Object.entries(pointsData)
  .map(([key, points]) => {
    const numericPoints = typeof points === 'number' ? points : 0;

    return {
      name: key === 'github' ? 'GitHub' : 
            key === 'x' || key === 'tweet' ? 'X Posts' :
            key === 'ai' || key === 'AIActivity' ? 'AI Activity' :
            key === 'gm' ? 'GM Streaks' :
            key === 'gn' ? 'GN Streaks' :
            key === 'sendit' ? 'SendIt' :
            key === 'role' ? 'Roles' : key.charAt(0).toUpperCase() + key.slice(1),
      points: numericPoints,
      color: key === 'github' ? '#9FA6B2' :
            key === 'x' || key === 'tweet' ? '#3EBEFF' :
            key === 'ai' || key === 'AIActivity' ? '#FFD964' :
            key === 'gm' ? '#FFD964' :
            key === 'gn' ? '#3EBEFF' :
            key === 'sendit' ? '#00FF9D' :
            '#00FF9D',
      icon: key === 'github' ? 'github' :
            key === 'x' || key === 'tweet' ? 'x' :
            key === 'ai' || key === 'AIActivity' ? 'ai' :
            key === 'role' ? 'users' :
            'activity'
    };
  })
  .filter(cat => cat.points > 0)
  .sort((a, b) => b.points - a.points)
  .slice(0, 3);

  // Level progression
  const currentLevelPoints = (userData.level || 0) * 1000;
  const nextLevelPoints = ((userData.level || 0) + 1) * 1000;
  const levelProgress = ((userData.points - currentLevelPoints) / (nextLevelPoints - currentLevelPoints)) * 100;
  const pointsToNext = nextLevelPoints - userData.points;
  
  // Determine gradient based on achievements
  const gradientClass = userData.points >= 10000 ? 'legendary' : 
                       userData.points >= 5000 ? 'epic' : 
                       userData.points >= 2500 ? 'rare' : 'common';
  
  // Stats breakdown
  const defaultBreakdown = {
    ai: Math.floor(userData.points * 0.3),
    github: Math.floor(userData.points * 0.2),
    x: Math.floor(userData.points * 0.15),
    gm: Math.floor(userData.points * 0.1),
    gn: Math.floor(userData.points * 0.1),
    sendit: Math.floor(userData.points * 0.1),
    role: Math.floor(userData.points * 0.05)
  };
  
  const stats = userData.pointsBreakdown || defaultBreakdown;

  // Get display avatar (enhanced from React component)
  const displayAvatar = userData?.xId !== "Not linked" && userData?.xId ? 
    `https://unavatar.io/x/${userData.xId}` : userData.avatarUrl;

  // Format numbers - show full number for better precision
  const formatNumber = (num: number) => {
    // Always show the full number with comma separators
    return num.toLocaleString();
  };

  // Generate SVG icons
  const getIcon = (iconName: string) => {
    const icons = {
      activity: '<path d="M22 12h-4l-3 9L9 3l-3 9H2"/>',
      sparkles: '<path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>',
      zap: '<polygon points="13,2 3,14 12,14 11,22 21,10 12,10 13,2"/>',
      users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
      github: '<path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>',
      x: '<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>',
      ai: '<rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 15h3M1 9h3M1 15h3"/>',
      crown: '<path d="M2 20h20l-2-6-3 2-5-4-5 4-3-2-2 6z"/><path d="M2 6l3 2 5-4 5 4 3-2"/>',
      shield: '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1v7z"/>',
      user: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
      globe: '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>'
    };
    return icons[iconName] || icons.activity;
  };

  // Prepare metadata for auto-updates
  const metadata = {
    points: userData.points,
    level: userData.level || 0,
    xId: userData.xId,
    githubId: userData.githubId,
    primaryName: userData.currentPrimaryName,
    undernameName: userData.undernameName,
    isVerified: false, 
    lastUpdate: Date.now(),
    version: "2.4"
  };

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>amPASSPORT Profile - ${userData.currentPrimaryName || userData.userId}</title>
    
    <!-- Enhanced Meta Tags for Social -->
    <meta name="description" content="${userData.levelTitle} · ${formatNumber(userData.points)} amPoints · Level ${userData.level} · amPortal Digital Identity">
    
    <!-- Open Graph Tags -->
    <meta property="og:title" content="amPASSPORT Profile - ${userData.currentPrimaryName || userData.userId}">
    <meta property="og:description" content="${userData.levelTitle} · ${formatNumber(userData.points)} amPoints · Level ${userData.level}">
    <meta property="og:image" content="https://res.cloudinary.com/dmw1bwmpr/image/upload/v1745139360/Main_Icon_qxmxdv.svg">
    <meta property="og:image:width" content="1200">
    <meta property="og:image:height" content="630">
    <meta property="og:url" content="https://amprofile.ar.io/profile/${userData.userId}">
    <meta property="og:type" content="profile">
    <meta property="og:site_name" content="amPortal">
    
    <!-- Twitter Card Tags -->
    <meta name="twitter:card" content="summary_large_image">
    <meta name="twitter:site" content="@amportal_ai">
    <meta name="twitter:creator" content="@amportal_ai">
    <meta name="twitter:title" content="amPASSPORT Profile - ${userData.currentPrimaryName || userData.userId}">
    <meta name="twitter:description" content="${userData.levelTitle} · ${formatNumber(userData.points)} amPoints · Level ${userData.level}">
    <meta name="twitter:image" content="https://res.cloudinary.com/dmw1bwmpr/image/upload/v1745139360/Main_Icon_qxmxdv.svg">
    
    <!-- Favicon -->
    <link rel="icon" type="image/svg+xml" href="https://res.cloudinary.com/dmw1bwmpr/image/upload/v1745139360/Main_Icon_qxmxdv.svg">
    <link rel="apple-touch-icon" href="https://res.cloudinary.com/dmw1bwmpr/image/upload/v1745139360/Main_Icon_qxmxdv.svg">
    
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300..800&display=swap');
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
        }
        
        :root {
            --primary-green: #00FF9D;
            --primary-cyan: #42C7FF;
            --primary-gold: #8FF4C8;
            --dark-bg: #0B110F;
            --darker-bg: #070B0A;
            --card-bg: #0E1412;
            --border-color: rgba(0, 255, 157, 0.12);
            --text-primary: #F4FFFA;
            --text-secondary: #A8B8B0;
            --text-muted: #7F9189;
            
            /* Dynamic gradient colors */
            --gradient-start: var(--primary-green);
            --gradient-end: var(--primary-cyan);
        }
        
        body {
            font-family: 'Manrope', -apple-system, sans-serif;
            background: ${isTransparent ? 'transparent' : 'var(--darker-bg)'};
            color: var(--text-primary);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow-x: hidden;
        }
        
        /* Main Container */
        .passport-wrapper {
            width: 100%;
            max-width: 480px;
            padding: 20px;
            perspective: 1000px;
        }
        
        /* Enhanced Glow Effect */
        .glow-container {
            position: relative;
            animation: float-entrance 1.2s ease-out;
        }
        
        @keyframes float-entrance {
            0% { opacity: 0; transform: translateY(50px); }
            100% { opacity: 1; transform: translateY(0); }
        }
        
        .glow-bg {
            position: absolute;
            inset: -32px;
            opacity: ${isTransparent ? '0.35' : '0.6'};
        }
        
        .glow-effect {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 100%;
            height: 100%;
            background: linear-gradient(45deg, 
                rgba(0, 255, 157, 0.25) 0%, 
                rgba(62, 190, 255, 0.20) 50%, 
                rgba(255, 217, 100, 0.25) 100%);
            border-radius: 50%;
            filter: blur(48px);
            animation: rotate-glow 25s linear infinite;
            display: ${isTransparent ? 'none' : 'block'};
        }
        
        @keyframes rotate-glow {
            0% { transform: translate(-50%, -50%) rotate(0deg) scale(1); }
            50% { transform: translate(-50%, -50%) rotate(180deg) scale(1.3); }
            100% { transform: translate(-50%, -50%) rotate(360deg) scale(1); }
        }
        
        /* Liquid Glass Card */
        .passport-card {
            position: relative;
            width: 100%;
            min-height: 580px; /* Increased height */
            border-radius: 24px;
            overflow: hidden;
            background: ${isTransparent ? 'linear-gradient(135deg, rgba(12, 16, 22, 0.85) 0%, rgba(8, 12, 18, 0.92) 100%)' : 'linear-gradient(135deg, rgba(16, 19, 31, 0.98) 0%, rgba(10, 14, 27, 0.99) 30%, rgba(20, 24, 36, 0.98) 70%, rgba(16, 19, 31, 0.98) 100%)'};
            backdrop-filter: blur(25px);
            border: 1px solid rgba(0, 255, 157, 0.15);
            box-shadow: 
                ${isTransparent ? '0 20px 40px rgba(0, 0, 0, 0.55)' : '0 25px 50px rgba(0, 0, 0, 0.6)'},
                ${isTransparent ? '0 30px 80px rgba(0, 255, 157, 0.12)' : '0 50px 100px rgba(0, 255, 157, 0.15)'},
                inset 0 1px 0 rgba(255, 255, 255, 0.1),
                inset 0 0 30px rgba(0, 255, 157, 0.05);
            transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
            transform-style: preserve-3d;
        }
        
        .passport-card:hover {
            transform: scale(1.02);
        }
        
        /* Gradient border based on status */
        .passport-card.legendary {
            --gradient-start: #FFD700;
            --gradient-end: #FF6B6B;
        }
        
        .passport-card.epic {
            --gradient-start: #B794F4;
            --gradient-end: #6B46C1;
        }
        
        .passport-card.rare {
            --gradient-start: #00FF9C;
            --gradient-end: #00D4FF;
        }
        
        /* Neural Network Background in Card */
        .card-neural-bg {
            position: absolute;
            inset: 0;
            opacity: 0.25;
            pointer-events: none;
        }
        
        /* Neural network animations */
        @keyframes pulse-node {
            0%, 100% { 
                opacity: 0.3; 
                transform: scale(1);
            }
            50% { 
                opacity: 0.8; 
                transform: scale(1.3);
            }
        }
        
        @keyframes pulse-line {
            0%, 100% { 
                opacity: 0.15;
                stroke-width: 0.5;
            }
            50% { 
                opacity: 0.4;
                stroke-width: 0.8;
            }
        }
        
        /* Holographic Overlay */
        .holo-overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(110deg, 
                transparent 35%, 
                rgba(0, 255, 157, 0.05) 42%, 
                rgba(255, 255, 255, 0.08) 50%, 
                rgba(62, 190, 255, 0.05) 58%, 
                transparent 65%);
            animation: holo-sweep 4s ease-in-out infinite;
            animation-delay: 4s;
            pointer-events: none;
        }
        
        @keyframes holo-sweep {
            0% { transform: translateX(-120%); }
            100% { transform: translateX(220%); }
        }
        
        /* Card Content */
        .passport-inner {
            position: relative;
            z-index: 10;
            padding: 24px;
            min-height: 100%;
            display: flex;
            flex-direction: column;
        }
        
        /* Header */
        .passport-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
        }
        
        .verified-status {
            display: flex;
            align-items: center;
            gap: 12px;
            opacity: 0;
            animation: slideInLeft 0.5s ease-out 0.3s forwards;
            position: relative;
            cursor: help;
        }
        
        .verified-status[title]:hover::after {
            content: attr(title);
            position: absolute;
            top: 100%;
            left: 0;
            margin-top: 8px;
            padding: 8px 12px;
            background: rgba(16, 19, 31, 0.95);
            border: 1px solid rgba(255, 107, 107, 0.3);
            border-radius: 8px;
            font-size: 12px;
            color: #FF6B6B;
            white-space: nowrap;
            z-index: 1000;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            backdrop-filter: blur(10px);
        }
        
        @keyframes slideInLeft {
            from { opacity: 0; transform: translateX(-20px); }
            to { opacity: 1; transform: translateX(0); }
        }
        
        .status-indicator {
            position: relative;
            width: 12px;
            height: 12px;
        }
        
        .status-dot {
            width: 12px;
            height: 12px;
            background: var(--primary-green);
            border-radius: 50%;
            animation: pulse-status 2s ease-in-out infinite;
            box-shadow: 0 0 12px rgba(0, 255, 157, 0.5);
        }
        
        .status-ping {
            position: absolute;
            inset: 0;
            width: 12px;
            height: 12px;
            background: var(--primary-green);
            border-radius: 50%;
            animation: ping-status 2s ease-in-out infinite;
        }
        
        @keyframes pulse-status {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
        }
        
        @keyframes ping-status {
            0% { transform: scale(1); opacity: 1; }
            100% { transform: scale(2); opacity: 0; }
        }
        
        .status-dot.unverified {
            background: #FF6B6B;
            box-shadow: 0 0 12px rgba(255, 107, 107, 0.5);
        }
        
        .status-ping.unverified {
            background: #FF6B6B;
        }
        
        .verified-text.unverified {
            color: #FF6B6B;
        }
        
        .verified-text {
            color: var(--primary-green);
            font-weight: 700;
            font-size: 14px;
            letter-spacing: 0.5px;
        }
        
        .passport-id {
            text-align: right;
            opacity: 0;
            animation: slideInRight 0.5s ease-out 0.3s forwards;
        }
        
        @keyframes slideInRight {
            from { opacity: 0; transform: translateX(20px); }
            to { opacity: 1; transform: translateX(0); }
        }
        
        .id-label {
            font-size: 12px;
            color: var(--text-secondary);
            font-weight: 500;
            margin-bottom: 4px;
        }
        
        .id-value {
            color: #B6FFDD;
            font-family: 'Manrope', -apple-system, sans-serif;
            font-weight: 700;
            font-size: 12px;
            line-height: 1.2;
        }
        
        /* Profile Section */
        .profile-section {
            display: flex;
            gap: 16px;
            margin-bottom: 20px;
            align-items: flex-start;
        }
        
        .avatar-container {
            position: relative;
        }
        
        .avatar-wrapper {
            position: relative;
            width: 80px;
            height: 80px;
            border-radius: 16px;
            overflow: hidden;
            transition: transform 0.3s ease;
        }
        
        .avatar-wrapper:hover {
            transform: scale(1.08);
        }
        
        .avatar-glow {
            position: absolute;
            inset: 0;
            background: linear-gradient(45deg, 
                rgba(0, 255, 157, 0.4) 0%, 
                rgba(62, 190, 255, 0.4) 100%);
            animation: pulse-avatar 3s ease-in-out infinite;
        }
        
        @keyframes pulse-avatar {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 0.8; }
        }
        
        .avatar-inner {
            position: relative;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, #1A1F2E 0%, #0A0E1B 100%);
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .avatar-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        
        .avatar-placeholder {
            width: 40px;
            height: 40px;
            color: var(--text-secondary);
        }
        
        .profile-info {
            flex: 1;
            min-width: 0;
        }
        
        .username {
            font-size: 18px;
            font-weight: 700;
            color: var(--text-primary);
            margin-bottom: 4px;
            opacity: 0;
            animation: fadeInUp 0.5s ease-out 0.4s forwards;
        }
        
        @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        .level-title {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 8px;
            opacity: 0;
            animation: fadeInUp 0.5s ease-out 0.5s forwards;
        }
        
        .crown-icon {
            width: 14px;
            height: 14px;
            color: #7EF3C1;
        }
        
        .level-text {
            color: #7EF3C1;
            font-weight: 500;
            font-size: 12px;
        }
        
        .member-since {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 11px;
            color: var(--text-muted);
            font-weight: 500;
            opacity: 0;
            animation: fadeInUp 0.5s ease-out 0.6s forwards;
        }
        
        .shield-icon {
            width: 12px;
            height: 12px;
        }
        
        .total-points {
            margin-top: 8px;
            opacity: 0;
            animation: scaleIn 0.5s ease-out 0.7s forwards;
        }
        
        @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.8); }
            to { opacity: 1; transform: scale(1); }
        }
        
        .points-value {
            font-size: 24px;
            font-weight: 700;
            color: var(--primary-green);
            line-height: 1;
        }
        
        .points-label {
            font-size: 12px;
            color: var(--text-secondary);
        }
        
        /* Top Categories */
        .categories-section {
            margin-bottom: 20px;
        }
        
        .categories-title {
            font-size: 12px;
            color: var(--text-secondary);
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-weight: 500;
            margin-bottom: 8px;
        }
        
        .category-item {
            position: relative;
            margin-bottom: 8px;
            border-radius: 8px;
            overflow: hidden;
            transition: all 0.3s ease;
            opacity: 0;
            animation: slideInCategory 0.5s ease-out forwards;
            background: rgba(20, 24, 47, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.08);
            backdrop-filter: blur(10px);
        }
        
        .category-item:nth-child(2) { animation-delay: 0.8s; }
        .category-item:nth-child(3) { animation-delay: 0.9s; }
        .category-item:nth-child(4) { animation-delay: 1.0s; }
        
        @keyframes slideInCategory {
            from { opacity: 0; transform: translateX(-20px); }
            to { opacity: 1; transform: translateX(0); }
        }
        
        .category-item:hover {
            transform: scale(1.02) translateX(4px);
        }
        
        .category-bg {
            position: absolute;
            inset: 0;
            background: var(--category-color, #00FF9D);
            opacity: 0.1;
        }
        
        .category-content {
            position: relative;
            padding: 10px;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        
        .category-left {
            display: flex;
            align-items: center;
            gap: 10px;
        }
        
        .category-icon-wrapper {
            width: 24px;
            height: 24px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .category-icon {
            width: 14px;
            height: 14px;
        }
        
        .category-name {
            font-size: 12px;
            font-weight: 500;
            color: var(--text-primary);
        }
        
        .category-points {
            font-size: 12px;
            font-weight: 700;
        }
        
        /* Stats section (hidden by default, shown on expand) */
        .stats-container {
            background: rgba(10, 14, 27, 0.4);
            border-radius: 16px;
            padding: 20px;
            margin-bottom: 20px;
            border: 1px solid var(--border-color);
            display: none;
        }
        
        /* Progress section */
        .progress-section {
            margin-bottom: 20px;
        }
        
        .progress-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
        }
        
        .progress-info {
            font-size: 12px;
            color: var(--text-secondary);
        }
        
        .next-level {
            font-size: 11px;
            color: var(--text-muted);
        }
        
        .progress-bar {
            height: 8px;
            background: rgba(138, 165, 162, 0.1);
            border-radius: 4px;
            overflow: hidden;
            position: relative;
        }
        
        .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
            border-radius: 4px;
            transition: width 1.5s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 0 12px rgba(0, 255, 156, 0.5);
            position: relative;
            overflow: hidden;
        }
        
        .progress-fill::after {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
            animation: shimmer 2s infinite;
        }
        
        @keyframes shimmer {
            0% { left: -100%; }
            100% { left: 100%; }
        }
        
        /* Badges section */
        .badges-section {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            margin-bottom: 20px;
        }
        
        .badge {
            padding: 6px 12px;
            background: rgba(138, 165, 162, 0.1);
            border: 1px solid rgba(138, 165, 162, 0.2);
            border-radius: 100px;
            font-size: 12px;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: all 0.3s ease;
            color: #FFFFFF;
        }
        
        .badge.verified {
            background: rgba(0, 255, 156, 0.1);
            border-color: rgba(0, 255, 156, 0.3);
            color: var(--primary-green);
        }
        
        .badge.og {
            background: rgba(255, 184, 0, 0.1);
            border-color: rgba(255, 184, 0, 0.3);
            color: #FFB800;
        }
        
        .badge.arns {
            background: rgba(0, 255, 157, 0.1);
            border-color: rgba(0, 255, 157, 0.3);
            color: var(--primary-green);
        }
        
        .badge svg {
            width: 14px;
            height: 14px;
            color: #FFFFFF;
        }
        
        /* Footer */
        .passport-footer {
            display: flex;
            flex-direction: column;
            gap: 12px;
            padding-top: 16px;
            border-top: 1px solid var(--border-color);
            margin-top: auto;
            align-items: center;
        }
        
        .footer-links {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
        }
        
        .portal-link {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            color: var(--primary-green);
            text-decoration: none;
            transition: opacity 0.3s ease;
            animation: breathe 3s ease-in-out infinite;
        }
        
        @keyframes breathe {
            0%, 100% { opacity: 0.8; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.05); }
        }
        
        .portal-link:hover {
            opacity: 1;
            animation: none;
        }
        
        .portal-logo {
            width: 20px;
            height: 20px;
        }
        
        .etch {
            font-size: 11px;
            color: var(--text-muted);
            font-family: 'Manrope', -apple-system, sans-serif;
            text-align: center;
        }
        
        .last-updated {
            margin-top: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0.6;
            transition: opacity 0.3s ease;
        }
        
        /* Bottom Accent */
        .bottom-accent {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 8px;
            background: linear-gradient(90deg, 
                var(--primary-green) 0%, 
                var(--primary-gold) 50%, 
                var(--primary-cyan) 100%);
            box-shadow: 0 0 16px rgba(0, 255, 157, 0.3);
            animation: accent-glow 3s ease-in-out infinite, scaleIn 1.5s ease-out 1.2s;
            transform-origin: left;
        }
        
        @keyframes accent-glow {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        
        /* Corner Accents */
        .corner-accent {
            position: absolute;
            width: 32px;
            height: 32px;
            border-radius: 8px;
        }
        
        .corner-tl {
            top: 16px;
            left: 16px;
            border-left: 2px solid rgba(0, 255, 157, 0.4);
            border-top: 2px solid rgba(0, 255, 157, 0.4);
            border-top-left-radius: 8px;
            box-shadow: 0 0 12px rgba(0, 255, 157, 0.2);
        }
        
        .corner-tr {
            top: 16px;
            right: 16px;
            border-right: 2px solid rgba(62, 190, 255, 0.4);
            border-top: 2px solid rgba(62, 190, 255, 0.4);
            border-top-right-radius: 8px;
            box-shadow: 0 0 12px rgba(62, 190, 255, 0.2);
        }
        
        .corner-bl {
            bottom: 16px;
            left: 16px;
            border-left: 2px solid rgba(255, 217, 100, 0.4);
            border-bottom: 2px solid rgba(255, 217, 100, 0.4);
            border-bottom-left-radius: 8px;
            box-shadow: 0 0 12px rgba(255, 217, 100, 0.2);
        }
        
        .corner-br {
            bottom: 16px;
            right: 16px;
            border-right: 2px solid rgba(0, 255, 157, 0.4);
            border-bottom: 2px solid rgba(0, 255, 157, 0.4);
            border-bottom-right-radius: 8px;
            box-shadow: 0 0 12px rgba(0, 255, 157, 0.2);
        }
        
        /* Floating Particles */
        .floating-particle {
            position: absolute;
            border-radius: 50%;
            animation: pulse-particle 3s ease-in-out infinite;
            box-shadow: 0 0 12px var(--particle-color);
            display: ${isTransparent ? 'none' : 'block'};
        }
        
        .particle-1 {
            top: -12px;
            right: -12px;
            width: 10px;
            height: 10px;
            background: var(--primary-green);
            --particle-color: rgba(0, 255, 157, 0.5);
            animation-delay: 0s;
        }
        
        .particle-2 {
            bottom: -8px;
            left: -8px;
            width: 12px;
            height: 12px;
            background: var(--primary-gold);
            --particle-color: rgba(255, 217, 100, 0.5);
            animation-delay: 1s;
        }
        
        .particle-3 {
            top: 50%;
            right: -10px;
            width: 8px;
            height: 8px;
            background: var(--primary-cyan);
            --particle-color: rgba(62, 190, 255, 0.5);
            animation-delay: 2s;
        }
        
        @keyframes pulse-particle {
            0%, 100% { opacity: 0.4; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.5); }
        }
        
        /* Mobile responsive */
        @media (max-width: 480px) {
            .passport-wrapper {
                padding: 16px;
                padding-top: 60px;
            }
            
            .hero-logo {
                width: 36px;
                height: 36px;
            }
            
            .passport-card {
                min-height: auto; /* Let content determine height on mobile */
            }
            
            .passport-inner {
                padding: 20px;
            }
            
            .username {
                font-size: 16px;
            }
            
            .points-value {
                font-size: 20px;
            }
            
            .stats-grid {
                grid-template-columns: repeat(2, 1fr);
            }
            
            /* Reduce particle sizes on mobile */
            .energy-orb {
                filter: blur(32px);
            }
            
            .orb-1 {
                width: 100px;
                height: 100px;
            }
            
            .orb-2 {
                width: 120px;
                height: 120px;
            }
            
            .orb-3 {
                width: 80px;
                height: 80px;
            }
        }
        
        /* Print styles for physical sharing */
        @media print {
            body {
                background: white;
            }
            
            .glow-effect, .floating-particle, .holo-overlay {
                display: none;
            }
            
            .passport-card {
                box-shadow: none;
        /* Neural network animations */
        .card-neural-bg circle {
            animation: pulse-node 4s ease-in-out infinite;
        }
        
        .card-neural-bg line {
            animation: pulse-line 4s ease-in-out infinite;
        }
        
        @keyframes pulse-node {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 0.8; transform: scale(1.2); }
        }
        
        @keyframes pulse-line {
            0%, 100% { opacity: 0.15; }
            50% { opacity: 0.4; }
        }
    </style>
</head>
<body>
    <div class="passport-wrapper">
        <div class="glow-container">
            <!-- Enhanced Glow Effect -->
            <div class="glow-bg">
                <div class="glow-effect"></div>
            </div>
            
            <!-- Main Passport Card with gradient class -->
            <div class="passport-card ${gradientClass}" id="passport-card">
                <!-- Neural Network Background in Card -->
                <div class="card-neural-bg">
                    <svg width="100%" height="100%" viewBox="0 0 400 580" preserveAspectRatio="none">
                        <!-- Nodes -->
                        ${Array(25).fill(0).map((_, i) => {
                          const x = Math.random() * 400;
                          const y = Math.random() * 580;
                          const delay = Math.random() * 3;
                          const duration = 4 + Math.random() * 2;
                          return `<circle cx="${x}" cy="${y}" r="2" fill="#00FF9D" opacity="0.3" style="animation: pulse-node ${duration}s ease-in-out infinite ${delay}s;"/>`;
                        }).join('')}
                        
                        <!-- Lines connecting nodes -->
                        ${(() => {
                          const nodes = Array(25).fill(0).map(() => ({
                            x: Math.random() * 400,
                            y: Math.random() * 580
                          }));
                          
                          return nodes.map((node, i) => {
                            return nodes.slice(i + 1).map((targetNode, j) => {
                              const distance = Math.sqrt(Math.pow(node.x - targetNode.x, 2) + Math.pow(node.y - targetNode.y, 2));
                              if (distance < 120) {
                                const delay = Math.random() * 3;
                                const duration = 4 + Math.random() * 2;
                                return `<line x1="${node.x}" y1="${node.y}" x2="${targetNode.x}" y2="${targetNode.y}" stroke="#00FF9D" stroke-width="0.5" opacity="0.15" style="animation: pulse-line ${duration}s ease-in-out infinite ${delay}s;"/>`;
                              }
                              return '';
                            }).join('');
                          }).join('');
                        })()}
                    </svg>
                </div>
                
                <!-- Holographic Overlay -->
                <div class="holo-overlay"></div>
                
                <!-- Card Content -->
                <div class="passport-inner">
                    <!-- Header -->
                    <div class="passport-header">
                        <div class="verified-status" title="Primary name mismatch - This profile must be accessed via the proper ArNS undername">
                            <div class="status-indicator">
                                <div class="status-dot unverified"></div>
                                <div class="status-ping unverified"></div>
                            </div>
                            <span class="verified-text unverified">UNVERIFIED</span>
                        </div>
                        
                        <div class="passport-id">
                            <div class="id-label">amPassport ID</div>
                            <div class="id-value">${userData?.processId?.slice(0, 12) || "PROCESSING..."}</div>
                        </div>
                    </div>
                    
                    <!-- Enhanced Profile Section -->
                    <div class="profile-section">
                        <div class="avatar-container">
                            <div class="avatar-wrapper">
                                <div class="avatar-glow"></div>
                                <div class="avatar-inner">
                                    ${displayAvatar 
                                        ? `<img src="${displayAvatar}" alt="Avatar" class="avatar-image" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
                                           <svg class="avatar-placeholder" style="display: none;" viewBox="0 0 24 24" fill="currentColor">
                                               ${getIcon('user')}
                                           </svg>`
                                        : `<svg class="avatar-placeholder" viewBox="0 0 24 24" fill="currentColor">
                                               ${getIcon('user')}
                                           </svg>`
                                    }
                                </div>
                            </div>
                        </div>
                        
                        <div class="profile-info">
                            <h3 class="username">${userData?.xId !== "Not linked" ? `@${userData.xId}` : userData?.userId?.slice(0, 8) || "User"}</h3>
                            
                            <div class="level-title">
                                <svg class="crown-icon" viewBox="0 0 24 24" fill="currentColor">
                                    ${getIcon('crown')}
                                </svg>
                                <span class="level-text">${userData.levelTitle || 'Digital Wanderer'}</span>
                            </div>
                            
                            <div class="member-since">
                                <svg class="shield-icon" viewBox="0 0 24 24" fill="currentColor">
                                    ${getIcon('shield')}
                                </svg>
                                <span>Member since ${joinDateFormatted}</span>
                            </div>
                            
                            <div class="total-points">
                                <div class="points-value">${formatNumber(userData?.points || 0)}</div>
                                <div class="points-label">Total AmPoints</div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Compact Top 3 Categories -->
                    <div class="categories-section">
                        <div class="categories-title">Top Points Categories</div>
                        ${topCategories.map((category, index) => {
                          const isFillIcon = category.icon === 'github' || category.icon === 'x';
                          return `
                            <div class="category-item" style="--category-color: ${category.color};">
                                <div class="category-bg"></div>
                                <div class="category-content">
                                    <div class="category-left">
                                        <div class="category-icon-wrapper" style="background-color: ${category.color}20;">
                                            <svg class="category-icon" viewBox="0 0 24 24" fill="${isFillIcon ? category.color : 'none'}" stroke="${isFillIcon ? 'none' : category.color}" stroke-width="${isFillIcon ? '0' : '2'}">
                                                ${getIcon(category.icon)}
                                            </svg>
                                        </div>
                                        <span class="category-name">${category.name}</span>
                                    </div>
                                    <div class="category-points" style="color: ${category.color};">
                                        ${formatNumber(category.points)}
                                    </div>
                                </div>
                            </div>
                        `;
                        }).join('')}
                    </div>
                    
                    <!-- Progress -->
                    <div class="progress-section">
                        <div class="progress-header">
                            <div class="progress-info">Level ${userData.level || 0} Progress</div>
                            <div class="next-level">${pointsToNext.toLocaleString()} to Level ${(userData.level || 0) + 1}</div>
                        </div>
                        <div class="progress-bar">
                            <div class="progress-fill" style="width: ${Math.min(levelProgress, 100)}%"></div>
                        </div>
                    </div>
                    
                    <!-- Badges -->
                    <div class="badges-section">
                        <div class="badge verified">
                            <svg viewBox="0 0 16 16" fill="currentColor">
                                <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/>
                            </svg>
                            Verified
                        </div>
                        ${userData.points >= 5000 ? '<div class="badge og">OG Member</div>' : ''}
                        ${userData.currentPrimaryName ? `
                            <div class="badge arns">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    ${getIcon('globe')}
                                </svg>
                                ${userData.currentPrimaryName}
                            </div>
                        ` : ''}
                        ${userData.xId && userData.xId !== 'Not linked' ? `
                            <div class="badge">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                                @${userData.xId}
                            </div>
                        ` : ''}
                        ${userData.githubId && userData.githubId !== 'Not linked' ? `
                            <div class="badge">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                                </svg>
                                @${userData.githubId}
                            </div>
                        ` : ''}
                        ${userData.xPostingsCount > 0 ? `
                            <div class="badge">
                                ${userData.xPostingsCount} X Activities
                            </div>
                        ` : ''}
                        ${userData.githubInteractionsCount > 0 ? `
                            <div class="badge">
                                ${userData.githubInteractionsCount} GitHub Activities
                            </div>
                        ` : ''}
                    </div>
                    
                    <!-- Footer -->
                    <div class="passport-footer">
                        <a href="https://x.com/amportal_ai" target="_blank" class="portal-link">
                            <img src="https://res.cloudinary.com/dmw1bwmpr/image/upload/v1745139360/Main_Icon_qxmxdv.svg" alt="amPortal" class="portal-logo">
                            <span>Follow amPortal</span>
                        </a>
                        
                        <div class="etch">Built on AO. Etched on Arweave.</div>
                        
                        <!-- Add last updated timestamp -->
                        <div class="last-updated">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style="opacity: 0.5;">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h2v-6h-2v6zm0-8h2V7h-2v2z"/>
                            </svg>
                            <span style="font-size: 9px; color: var(--text-muted); opacity: 0.5; margin-left: 4px;">
                                Last updated: ${new Date().toLocaleString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric', 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                })}
                            </span>
                        </div>
                    </div>
                </div>
                
                <!-- Bottom Accent -->
                <div class="bottom-accent"></div>
                
                <!-- Corner Accents -->
                <div class="corner-accent corner-tl"></div>
                <div class="corner-accent corner-tr"></div>
                <div class="corner-accent corner-bl"></div>
                <div class="corner-accent corner-br"></div>
            </div>
            
            <!-- Floating Particles -->
            <div class="floating-particle particle-1"></div>
            <div class="floating-particle particle-2"></div>
            <div class="floating-particle particle-3"></div>
        </div>
    </div>
    
    <script>
        // Check verification status based on URL
        window.addEventListener('DOMContentLoaded', () => {
            const hostname = window.location.hostname.toLowerCase();
            const primaryName = '${userData.currentPrimaryName || ''}';
            
            if (primaryName) {
                const primaryNameLower = primaryName.toLowerCase();
                
                // Valid format: amprofile_primaryname.ar.io or similar
                const expectedHostname = \`amprofile_\${primaryNameLower}\`;
                const isValidAmpassportUrl = (
                    hostname === \`\${expectedHostname}.ar.io\` ||
                    hostname === \`\${expectedHostname}.arweave.net\` ||
                    hostname === \`\${expectedHostname}.arweave.dev\` ||
                    hostname.startsWith(\`\${expectedHostname}.\`)
                );
                
                const verifiedStatus = document.querySelector('.verified-status');
                const statusDot = document.querySelector('.status-dot');
                const statusPing = document.querySelector('.status-ping');
                const verifiedText = document.querySelector('.verified-text');
                
                // Update verification status based on URL
                if (isValidAmpassportUrl) {
                    // Valid URL - show as verified
                    verifiedStatus?.removeAttribute('title');
                    statusDot?.classList.remove('unverified');
                    statusPing?.classList.remove('unverified');
                    verifiedText?.classList.remove('unverified');
                    if (verifiedText) verifiedText.textContent = 'VERIFIED';
                } else {
                    // Invalid URL - show as unverified
                    verifiedStatus?.setAttribute('title', 'Primary name mismatch - This profile must be accessed via the proper ArNS undername');
                    statusDot?.classList.add('unverified');
                    statusPing?.classList.add('unverified');
                    verifiedText?.classList.add('unverified');
                    if (verifiedText) verifiedText.textContent = 'UNVERIFIED';
                }
                
                console.log('Verification check:', {
                    hostname,
                    expectedHostname,
                    primaryName: primaryNameLower,
                    isValidAmpassportUrl
                });
            } else {
                // No primary name - can't be verified
                const verifiedText = document.querySelector('.verified-text');
                if (verifiedText) verifiedText.textContent = 'UNVERIFIED';
            }
        });
        
        // Enhanced 3D hover effect - removed blur, kept sharpness
        const card = document.getElementById('passport-card');
        
        card?.addEventListener('mousemove', (e) => {
            const rect = card.getBoundingClientRect();
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;
            const rotateY = ((e.clientX - rect.left - centerX) / centerX) * 6;
            const rotateX = ((centerY - (e.clientY - rect.top)) / centerY) * 2;
            
            card.style.transform = \`perspective(1000px) rotateY(\${rotateY}deg) rotateX(\${rotateX}deg) scale(1.02)\`;
        });
        
        card?.addEventListener('mouseleave', () => {
            card.style.transform = 'perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1)';
        });
        
        // Animate progress bar on load
        window.addEventListener('load', () => {
            const progressFill = document.querySelector('.progress-fill');
            if (progressFill) {
                setTimeout(() => {
                    progressFill.style.width = '${Math.min(levelProgress, 100)}%';
                }, 100);
            }
        });
        
        // Metadata for updates
        console.log('amPassport Profile Loaded', {
            user: '${userData.userId}',
            points: ${userData.points},
            level: ${userData.level || 0},
            primaryName: '${userData.currentPrimaryName || ''}',
            currentUrl: window.location.href,
            timestamp: new Date().toISOString()
        });
    </script>
    <!-- METADATA: ${btoa(JSON.stringify(metadata))} -->
</body>
</html>`;
};
